import { io, Socket } from "socket.io-client";
import environment from "../../config/environment";
import {
  ChatResponsePayload,
  SendMessagePayload,
} from "../../screens/chatbot/ChatBot.types";
import BaseInstance from "../BaseInstance";
import { UserRoles } from "../../entities/IUser";

// Types for API responses
export interface SessionMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string;
}

export interface Session {
  sessionId: string;
  userId: string;
  status: string;
  startedAt: string; // ISO string
  lastActivityAt: string; // ISO string
  duration: number; // sekunde
  messageCount: number;
  totalTokens: number;
  totalCost: number;
  conversationName: string | null;
}

export interface Response<T> {
  data: T[];
  limit: number;
  page: number;
  total: number;
}

export interface ActiveExportPayload {
  exportId: string;
  exportType: string;
  status: string;
  progressMessage: string | null;
  progressPercent: number | null;
  currentPage: number | null;
  totalPages: number | null;
  progressUpdatedAt: string | null;
}

export interface SessionMessagesResponse {
  messages: SessionMessage[];
  session?: unknown;
  activeExport?: ActiveExportPayload | null;
}

// Socket Service Class (manages WebSocket connection)
// Uses the same host as the API (goes through aurora-a1-api)
class ChatSocketService {
	private socket: Socket | null = null;
	private readonly SOCKET_URL: string;

	constructor() {
		// Use the same host/protocol as the API, with /socket.io path for Socket.IO
		// This ensures both HTTP API and WebSocket go through aurora-a1-api
		this.SOCKET_URL = `${environment.api.protocol}://${environment.api.host}${
			environment.api.port ? ':' + environment.api.port : ''
		}`;
	}

	connect(token?: string): void {
		if (this.socket?.connected) {
			return;
		}

		// Connect to the /chat namespace on aurora-a1-api
		this.socket = io(`${this.SOCKET_URL}/chat`, {
			transports: ['websocket'],
			auth: {
				token: token || '',
			},
		});

		this.socket.on('connect', () => {
			console.log('Socket connected:', this.socket?.id);
		});

		this.socket.on('disconnect', () => {
			console.log('Socket disconnected');
		});

		this.socket.on('connect_error', (error) => {
			console.error('Socket connection error:', error);
		});
	}

	disconnect(): void {
		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
		}
	}

	sendMessage(data: SendMessagePayload): void {
		if (!this.socket) {
			throw new Error('Socket not connected');
		}
		this.socket.emit('chat:message', data);
	}

	subscribeSession(sessionId: string, token: string): void {
		if (!this.socket) {
			return;
		}
		this.socket.emit('chat:subscribe', { sessionId, token });
	}

	sendFeedback(data: any): void {
		if (!this.socket) {
			throw new Error('Socket not connected');
		}
		this.socket.emit('chat:feedback', data);
	}

	onMessage(callback: (data: ChatResponsePayload) => void): void {
		if (!this.socket) return;
		this.socket.on('chat:response', callback);
	}

	onProgress(
		callback: (data: {
			message?: string;
			status: string;
			exportId?: string;
			sessionId?: string;
			percentage?: number;
			totalCount?: number;
			currentPage?: number;
			totalPages?: number;
			downloadUrl?: string;
			reportId?: string;
		}) => void
	): void {
		if (!this.socket) return;
		this.socket.on('export:progress', callback);
	}

	onCancell(callback: (data: { message: string; sessionId?: string }) => void): void {
		if (!this.socket) return;
		this.socket.on('export:cancelled', callback);
	}

	onError(callback: (error: { message: string }) => void): void {
		if (!this.socket) return;
		this.socket.on('chat:error', callback);
	}

	onFeedbackSaved(callback: (data: { success: boolean }) => void): void {
		if (!this.socket) return;
		this.socket.on('chat:feedback:saved', callback);
	}

	onConnect(callback: () => void): void {
		if (!this.socket) return;
		this.socket.on('connect', callback);
	}

	onDisconnect(callback: () => void): void {
		if (!this.socket) return;
		this.socket.on('disconnect', callback);
	}

	removeListener(event: string, callback?: (...args: any[]) => void): void {
		if (!this.socket) return;
		if (callback) {
			this.socket.off(event, callback);
		} else {
			this.socket.off(event);
		}
	}

	get isConnected(): boolean {
		return this.socket?.connected || false;
	}
}

const ChatGateway = {
	api: {
		/**
		 * Get session messages from ChatBot API
		 * Route: GET /chat/sessions/:sessionId/messages
		 * Proxied through aurora-a1-api to chatbot backend
		 */
		getSessionMessages: async (
			sessionId: string
		): Promise<SessionMessagesResponse> => {
			return BaseInstance.get(`/chat/sessions/${sessionId}/messages`);
		},
		getAllSessions: async (userId: number): Promise<Response<Session>> => {
			return BaseInstance.get(`chat/observability/sessions?userId=${userId}`);
		},
		updateSessionName: async (
			sessionId: string,
			conversationName: string
		): Promise<Session> => {
			return BaseInstance.patch(
				`chat/observability/sessions/${sessionId}/name`,
				{
					conversationName: conversationName,
				}
			);
		},
		deleteSession: async (sessionId: string): Promise<void> => {
			return BaseInstance.delete(`chat/observability/sessions/${sessionId}`);
		},
		getTableReport: async (
			reportId: string,
			skip: number = 0,
			limit: number = 10
		): Promise<any> => {
			return BaseInstance.get(
				`/chat/sessions/tables/${reportId}?skip=${skip}&limit=${limit}`
			);
		},
		getSignatureComparisonReport: async (reportId: string): Promise<any> => {
			return BaseInstance.get(`/chat/sessions/reports/download/${reportId}`);
		},
		cancelExportJob: async (exportId: string): Promise<any> => {
			return BaseInstance.post(`/chat/exports/${exportId}/cancel`);
		},
		getChatAccessRoles: async (): Promise<UserRoles[]> => {
			return BaseInstance.get(`/chat/allowed-roles`);
		},
		fraudControlCheck: async (file: File, sessionId: string | null): Promise<{ accepted: boolean; sessionId: string }> => {
			const csvContent = await file.text();
			return BaseInstance.post(`/chat/fraud-control/check`, { csvContent, sessionId });
		},
	},
	socket: new ChatSocketService(),
};

export default ChatGateway;
